/**
 * Mountain Brook's pink Building Permit Application, as PDF (ADR 0289).
 *
 * Every box on the city's paper, drawn clean, and only the ones Jonathon
 * filled by hand filled in. This is the file saved to Docs and uploaded on
 * the city's form.
 *
 * Change one, change both: `PermitPinkSheet` draws the same sheet on screen
 * for the office to check, and nothing compares the two.
 */

import {
  permitAmountText,
  permitDateText,
  type PermitApplication,
} from "@roof-hawk/core";

import { Pen, PAGE_WIDTH } from "./kit";

const L = 29;
const R = PAGE_WIDTH - 29;
const MID = 306;

function header(pen: Pen) {
  pen.text("City of Mountain Brook", L, 40, { size: 8.5 });
  pen.text("BUILDING PERMIT APPLICATION", 196, 40, {
    size: 12,
    font: pen.fonts.bold,
  });
  pen.text("56 Church Street, Mountain Brook AL 35213", 436, 40, { size: 7 });
  pen.y = 40;
  pen.paragraph(
    "(Permit Fee: Residential-$11.00 per $1000 Commercial-$12.00 per $1000 " +
      "plus a $50.00 issuance fee MINIMUM FEE OF $100.00) and a $20.00 State " +
      "Trade Academy flat fee",
    L,
    440,
    { size: 9 },
  );
  pen.frame(500, 46, R - 500, 24);
  pen.text("Rcvd by: ________", 504, 56, { size: 7 });
  pen.text("Date: ___________", 504, 66, { size: 7 });
  pen.text("RV Acct # ____________", 400, 78, { size: 8 });
  pen.y = 80;
  pen.paragraph(
    "If the scope and/or nature of the work as listed on the original permit " +
      "application changes OR if the cost of construction exceeds the amount " +
      "upon which the permit is based a revised application must be submitted " +
      "and any additional dues owed must paid. (§109-44i)",
    L,
    R - L,
    { size: 6.8, bold: true },
  );
  pen.paragraph(
    "Application is hereby made for a building permit to accomplish the work " +
      "as herein described in accordance with duplicate plans and/or " +
      "specifications necessary for compliance shall be observed and all " +
      "requirements of the building code, zoning code, and all other pertinent " +
      "laws and ordinances of the City of Mountain Brook regulating " +
      "construction shall be complied pursuit of this work whether or not " +
      "specified herein. REINSPECTION FEE $100.00 - 2021 IBC and 2021 IRC",
    L,
    R - L,
    { size: 6.3 },
  );
}

function jobLocation(pen: Pen, form: PermitApplication) {
  const y = pen.y + 14;
  pen.centre("JOB LOCATION", L, R, y);
  pen.field("Address:", form.site.street, L, R, y + 14);
  pen.field("Legal Description: Lot:", "", L, 260, y + 28);
  pen.field("Block", "", 268, 420, y + 28);
  pen.field("Survey", "", 428, R, y + 28);
  pen.y = y + 28;
}

function identification(pen: Pen, form: PermitApplication) {
  const top = pen.y + 14;
  pen.centre("IDENTIFICATION", L, R, top);
  const box = top + 4;
  pen.frame(L, box, R - L, 82);
  pen.line(MID, box, MID, box + 82);
  const o = form.owner;
  const c = form.contractor;
  const left = L + 4;
  const right = MID + 4;
  pen.field("Name of Owner:", o.name, left, MID - 6, box + 13);
  pen.field("Address", o.address, left, MID - 6, box + 26);
  pen.field("City", o.city, left, 214, box + 39);
  pen.field("State", o.state, 220, MID - 6, box + 39);
  pen.field("Phone", o.phone, left, MID - 6, box + 52);
  pen.field("Name of Contractor", c.name, right, R - 4, box + 13);
  pen.field("Address", c.address, right, R - 4, box + 26);
  pen.field("City", c.city, right, 490, box + 39);
  pen.field("State", c.state, 496, R - 4, box + 39);
  pen.field("Phone", c.phone, right, R - 4, box + 52);
  pen.field("Mtn Brook Lic #", c.cityLicenseNumber, right, 460, box + 65);
  pen.field("State Lic#", c.licenseNumber, 466, R - 4, box + 65);
  pen.y = box + 82;
}

function plans(pen: Pen) {
  const box = pen.y;
  pen.frame(L, box, R - L, 74);
  pen.line(MID, box, MID, box + 74);
  const left = L + 4;
  pen.text("Plans Drawn By:", left, box + 11, { font: pen.fonts.bold });
  let x = pen.box(left, box + 22, "Architect");
  x = pen.box(x, box + 22, "Engineer");
  pen.box(x, box + 22, "Designer");
  pen.field("Name:", "", left, MID - 6, box + 35);
  pen.field("Address:", "", left, MID - 6, box + 47);
  pen.field("Phone:", "", left, MID - 6, box + 59);
  pen.field("State Registration #", "", left, MID - 6, box + 71);
  const right = MID + 4;
  const end = pen.text(
    "Is Architect or Engineering supervision included?",
    right,
    box + 11,
  );
  const yes = pen.box(end + 4, box + 11, "Yes");
  pen.box(yes, box + 11, "No", true);
  const arch = pen.box(right, box + 23, "Architect");
  pen.box(arch, box + 23, "Engineer");
  pen.field("If yes, by whom?", "", right, R - 4, box + 47);
  pen.field("Phone:", "", right, R - 4, box + 59);
  pen.field("State Registration#", "", right, R - 4, box + 71);
  pen.y = box + 74;
}

function ticks(
  pen: Pen,
  x: number,
  top: number,
  labels: string[],
  size = 7.5,
  step = 10,
) {
  labels.forEach((label, i) => pen.box(x, top + i * step, label, false, size));
}

function improvement(pen: Pen, form: PermitApplication) {
  const box = pen.y;
  const c1 = 200;
  const c2 = 330;
  pen.frame(L, box, R - L, 96);
  pen.line(c1, box, c1, box + 96);
  pen.line(c2, box, c2, box + 96);
  const left = L + 4;
  pen.text("Type Improvement Permit is for:", left, box + 11, {
    font: pen.fonts.bold,
  });
  const newBuild = !!form.newConstruction;
  pen.box(left, box + 22, "New Construction", newBuild);
  ticks(pen, left, box + 32, ["Addition", "Alterations"]);
  pen.box(left, box + 52, "Repair", !newBuild);
  pen.box(left, box + 62, "Other");
  pen.field("", form.workType, left, c1 - 8, box + 76);
  pen.text("Type Construction", c1 + 4, box + 11, { font: pen.fonts.bold });
  ticks(pen, c1 + 4, box + 22, [
    "Fireproof",
    "Fire Resistive",
    "Heavy Timber",
    "Non-Combustible",
    "Ordinary",
    "Other ________",
  ]);
  pen.text("Occupancy (Use)", c2 + 4, box + 11, { font: pen.fonts.bold });
  pen.text("NONRESIDENTIAL", c2 + 4, box + 21, {
    size: 7,
    font: pen.fonts.bold,
  });
  ticks(
    pen,
    c2 + 4,
    box + 30,
    [
      "Amusement, Recreational",
      "Church/Religious",
      "Business",
      "Service Station/Repair Garage",
      "Hospital/Institutional",
      "Office/Professional",
      "School/Library/Educational",
      "Other",
    ],
    6.3,
    8.4,
  );
  pen.text("RESIDENTIAL", 480, box + 21, { size: 7, font: pen.fonts.bold });
  ticks(
    pen,
    480,
    box + 30,
    ["Single Family", "Duplex", "Apartment - # of Units __"],
    6.3,
    8.4,
  );
  pen.y = box + 96;
}

function characteristics(pen: Pen) {
  const top = pen.y + 12;
  pen.centre("SELECTED CHARACTERISTICS OF BUILDING", L, R, top);
  const box = top + 4;
  pen.frame(L, box, R - L, 118);
  pen.line(MID, box, MID, box + 118);
  pen.line(L, box + 68, R, box + 68);
  const left = L + 4;
  const rows = [
    ["Type of heat:", "Gas", "Oil", "Electric", "Coal", "Other"],
    ["Type of sewage disposal:", "Sanitary Sewer", "Septic"],
    ["Type of water supply:", "Public", "Individual (well, etc)"],
    ["Will there be central air?", "Yes", "No"],
    ["Will there be an elevator?", "Yes", "No"],
  ];
  rows.forEach(([label = "", ...options], i) => {
    let x = pen.text(label, left, box + 11 + i * 10, { size: 6.8 }) + 4;
    for (const option of options)
      x = pen.box(x, box + 11 + i * 10, option, false, 6.5);
  });
  pen.text(
    "Will there be off street parking? # of spaces? ____",
    left,
    box + 61,
    {
      size: 6.8,
    },
  );
  const right = MID + 4;
  pen.text("Dimensions", right, box + 11, { size: 7.5, font: pen.fonts.bold });
  pen.field("Number of stories", "", right + 10, R - 6, box + 24, 7);
  pen.field("Total sq ft living area", "", right + 10, R - 6, box + 36, 7);
  pen.field(
    "Total sq ft of non-living area",
    "",
    right + 10,
    R - 6,
    box + 48,
    7,
  );
  pen.text("Residential Buildings Only:", left, box + 78, {
    size: 7.5,
    font: pen.fonts.bold,
  });
  pen.field("Number of Bedrooms", "", left + 10, MID - 30, box + 91, 7);
  pen.field("Number of Bathrooms", "", left + 10, MID - 30, box + 102, 7);
  pen.field("Total Number of Rooms", "", left + 10, MID - 30, box + 113, 7);
  pen.text("Plot and Zoning Information:", right, box + 78, {
    size: 7.5,
    font: pen.fonts.bold,
  });
  pen.field("Plot area (in sq feet)", "", right + 10, R - 30, box + 91, 7);
  pen.field(
    "Plot width: Front ______ Rear",
    "",
    right + 10,
    R - 30,
    box + 102,
    7,
  );
  pen.field("Length of sides 1 ______ 2", "", right + 10, R - 30, box + 113, 7);
  pen.y = box + 118;
}

function subcontractors(pen: Pen) {
  const box = pen.y;
  pen.frame(L, box, R - L, 36);
  pen.line(MID, box, MID, box + 36);
  pen.y = box;
  pen.paragraph(
    "SUBCONTRACTORS: Please list ALL subcontractors to be used on job on the " +
      "BACK of this form. List names, address, and phone number of subs",
    L + 4,
    MID - L - 10,
    { size: 7.5, bold: true },
  );
  pen.y = box;
  pen.paragraph(
    "By signing below I understand that it is the contractor's responsibility " +
      "to call the City and schedule all inspections. Failure to do so may " +
      "result in future permit applications being denied.",
    MID + 4,
    R - MID - 10,
    { size: 7.5, bold: true },
  );
  pen.y = box + 36;
}

function certification(pen: Pen, form: PermitApplication) {
  const top = pen.y + 12;
  pen.centre("CERTIFICATION", L, R, top);
  pen.y = top;
  pen.paragraph(
    "I hereby certify that I have read this application and that all " +
      "information contained herein is true and correct, that I agree to " +
      "comply with all city ordinances and state laws regulating building " +
      "construction, that I am the owner or the authorized agent for the work " +
      "herein described and that the total contract or valuations is:",
    L,
    R - L,
    { size: 6.5 },
  );
  const y = pen.y + 15;
  pen.field("$", permitAmountText(form.contractAmount), L, 220, y);
  pen.field(
    "Proposed Start Date:",
    permitDateText(form.proposedStart),
    228,
    400,
    y,
  );
  pen.field("Proposed End Date:", permitDateText(form.proposedEnd), 408, R, y);
  pen.field("Name of Company:", form.contractor.name, L, 400, y + 15);
  pen.field("Date:", permitDateText(form.preparedOn), 408, R, y + 15);
  pen.signature("Signature:", form.contractor.signerName, L, 360, y + 31);
  pen.field("Printed Name:", form.contractor.signerName, 368, R, y + 31);
  pen.line(L, y + 37, R, y + 37, 1.4);
  pen.centre("For office use only", L, R, y + 47, 8);
  pen.field("Approved by:", "", L, 220, y + 60);
  pen.field("Job Cost:", "", 228, 400, y + 60);
  pen.field("Fire Marshal:", "", 408, R, y + 60);
  pen.field("Permit Fee:", "", L, 220, y + 73);
  pen.field("Date Issued:", "", 228, 400, y + 73);
  pen.field("Permit #", "", 408, R, y + 73);
}

/** The whole pink sheet, on a page already painted pink. */
export function drawPinkSheet(pen: Pen, form: PermitApplication) {
  header(pen);
  jobLocation(pen, form);
  identification(pen, form);
  plans(pen);
  improvement(pen, form);
  characteristics(pen);
  subcontractors(pen);
  certification(pen, form);
}
